import { useRef } from "react";

import {
  captureComposerDraftContent,
  type ComposerImageAttachment,
  type ComposerFileAttachment,
  useComposerDraftStore,
} from "../../composerDraftStore";
import type { DraftId } from "../../draftId";
import { shortcutLabelForCommand } from "../../keybindings";
import { withComposerStashDefault } from "../../composerStashKeybindings";
import { useServerKeybindings } from "../../rpc/serverState";
import { useComposerStashMenu } from "../chat/useComposerStashController";
import { toastManager } from "../ui/toast";
import { useLandingComposerDraftId } from "./useLandingComposerDraftId";
import { useSeededComposerPrompt } from "./useSeededComposerPrompt";

/**
 * Owns the persisted content behind the landing composer.
 *
 * A new thread is created asynchronously. The source content must remain until
 * that target draft exists and has received it, so this hook holds the exact
 * source snapshot taken at send time and clears only that unchanged snapshot.
 */
export function useLandingComposerDraftState() {
  const landingDraftId = useLandingComposerDraftId();
  const landingDraft = useComposerDraftStore((state) => state.getComposerDraft(landingDraftId));
  const submissionSnapshotRef = useRef<ReturnType<typeof captureComposerDraftContent> | null>(null);
  const keybindings = withComposerStashDefault(useServerKeybindings());
  const composer = useSeededComposerPrompt({
    prompt: landingDraft?.prompt ?? "",
    onPromptChange: (nextPrompt) =>
      useComposerDraftStore.getState().setPrompt(landingDraftId, nextPrompt),
  });
  const stash = useComposerStashMenu({
    target: landingDraftId,
    onNotice: (message, type) => toastManager.add({ title: message, type }),
    onSaved: () => composer.onPromptChange("", 0),
    onRestored: () => {
      const restored = useComposerDraftStore.getState().getComposerDraft(landingDraftId);
      if (restored) composer.onPromptChange(restored.prompt, restored.prompt.length);
    },
  });

  return {
    landingDraftId,
    landingDraft,
    ...composer,
    keybindings,
    stash,
    onStashNotice: (message: string, type: "error" | "success" | "warning") =>
      toastManager.add({ title: message, type }),
    stashShortcutLabel: shortcutLabelForCommand(keybindings, "composer.stash", {
      context: { terminalFocus: false, terminalOpen: false },
    }),
    onImagesChange: (nextImages: ComposerImageAttachment[]) =>
      syncLandingDraftImages(landingDraftId, nextImages),
    onFilesChange: (nextFiles: ComposerFileAttachment[]) =>
      useComposerDraftStore.getState().setFiles(landingDraftId, nextFiles),
    onRemoveTerminalContext: (contextId: string) =>
      useComposerDraftStore.getState().removeTerminalContext(landingDraftId, contextId),
    onSubmissionStarted: () => {
      const draft = useComposerDraftStore.getState().getComposerDraft(landingDraftId);
      submissionSnapshotRef.current = draft ? captureComposerDraftContent(draft) : null;
    },
    onSubmissionHydrated: () => {
      const snapshot = submissionSnapshotRef.current;
      submissionSnapshotRef.current = null;
      if (snapshot)
        useComposerDraftStore.getState().clearComposerContentIfUnchanged(landingDraftId, snapshot);
    },
  };
}

function syncLandingDraftImages(
  landingDraftId: DraftId,
  nextImages: ComposerImageAttachment[],
): void {
  const store = useComposerDraftStore.getState();
  const stored = store.getComposerDraft(landingDraftId)?.images ?? [];
  const storedIds = new Set(stored.map((image) => image.id));
  const imageIds = new Set(nextImages.map((image) => image.id));
  const added = nextImages.filter((image) => !storedIds.has(image.id));
  if (added.length > 0) store.addImages(landingDraftId, added);
  for (const image of stored) {
    if (!imageIds.has(image.id)) store.removeImage(landingDraftId, image.id);
  }
}
